import type { Coordinates3D } from './common';
import type { LocationType } from './warehouse';

export const VELOCITY_CLASSES = ['A', 'B', 'C'] as const;
/** ABC by units shipped per day: A = fast mover, C = slow mover. */
export type VelocityClass = (typeof VELOCITY_CLASSES)[number];

/** Directed-putaway rules of a warehouse (warehouses.settings.putaway). */
export interface PutawayPolicy {
  /** Allow stock of different sellers in one bin. Default false: a bin belongs to one seller at a time. */
  readonly allowMixedTenants: boolean;
  /** Allow different products in one bin. */
  readonly allowMixedProducts: boolean;
  /** Units shipped per day from which a product is class A / B (below B it is C). */
  readonly velocityA: number;
  readonly velocityB: number;
  /** Items at or above this unit weight (g) belong on the lowest level. */
  readonly heavyItemG: number;
  /** Ergonomic levels (waist to shoulder) reserved for fast movers. */
  readonly goldenLevels: readonly [number, number];
}

export const DEFAULT_PUTAWAY_POLICY: PutawayPolicy = {
  allowMixedTenants: false,
  allowMixedProducts: true,
  velocityA: 5,
  velocityB: 1,
  heavyItemG: 15_000,
  goldenLevels: [2, 3],
};

export function velocityClass(unitsPerDay: number, policy: PutawayPolicy): VelocityClass {
  if (unitsPerDay >= policy.velocityA) return 'A';
  if (unitsPerDay >= policy.velocityB) return 'B';
  return 'C';
}

/** Goods waiting at the dock. */
export interface PutawayItem {
  readonly tenantId: string;
  readonly productId: string;
  readonly lotNo: string | null;
  readonly expiryDate: string | null;
  readonly qty: number;
  /** null while the product is unmeasured: capacity rules are skipped */
  readonly unitVolumeCm3: number | null;
  readonly unitWeightG: number | null;
  readonly velocity: VelocityClass;
}

export interface BinContent {
  readonly tenantId: string;
  readonly productId: string;
  readonly lotNo: string | null;
  readonly expiryDate: string | null;
  readonly qty: number;
}

/** A storage bin and what is in it right now. */
export interface BinSnapshot {
  readonly locationId: string;
  readonly code: string;
  readonly type: LocationType;
  readonly level: number;
  readonly coordinates: Coordinates3D | null;
  readonly maxVolumeCm3: number | null;
  readonly maxWeightG: number | null;
  readonly usedVolumeCm3: number;
  readonly usedWeightG: number;
  readonly contents: readonly BinContent[];
  readonly isActive: boolean;
  readonly isBlocked: boolean;
}

export const PUTAWAY_REASONS = [
  'same_lot',
  'same_product',
  'empty_bin',
  'pick_face_for_fast_mover',
  'bulk_for_slow_mover',
  'near_depot',
  'far_from_depot',
  'golden_zone',
  'heavy_low_level',
  'fits_tightly',
  'chosen_by_manager',
] as const;
export type PutawayReason = (typeof PUTAWAY_REASONS)[number];

export const PUTAWAY_REASON_LABELS: Readonly<Record<PutawayReason, string>> = {
  same_lot: 'Aynı lot zaten bu gözde',
  same_product: 'Aynı ürün bu gözde',
  empty_bin: 'Boş göz',
  pick_face_for_fast_mover: 'Hızlı ürün toplama gözüne',
  bulk_for_slow_mover: 'Yavaş ürün yedek alana',
  near_depot: 'Paketleme alanına yakın',
  far_from_depot: 'Yakın gözler hızlı ürünlere kalır',
  golden_zone: 'Ergonomik kat',
  heavy_low_level: 'Ağır ürün alt kata',
  fits_tightly: 'Gözü verimli dolduruyor',
  chosen_by_manager: 'Yönetici bu gözü seçti',
};

export const PUTAWAY_REJECTIONS = [
  'inactive',
  'blocked',
  'not_storage',
  'other_seller',
  'other_product',
  'volume_exceeded',
  'weight_exceeded',
] as const;
export type PutawayRejection = (typeof PUTAWAY_REJECTIONS)[number];

export const PUTAWAY_REJECTION_LABELS: Readonly<Record<PutawayRejection, string>> = {
  inactive: 'Lokasyon pasif',
  blocked: 'Lokasyon sayım veya bakım için kilitli',
  not_storage: 'Bu lokasyon depolama gözü değil',
  other_seller: 'Bu gözde başka bir satıcının ürünü var',
  other_product: 'Bu gözde başka bir ürün var',
  volume_exceeded: 'Göz hacmi yetmiyor',
  weight_exceeded: 'Göz taşıma kapasitesi yetmiyor',
};

/**
 * The bin holds another seller's stock, or another product where the warehouse keeps products
 * apart: a warning anyone on the floor may confirm ("Yine de bu göze koy"), never a refusal. The
 * suggestions still keep such bins out.
 */
export const MIXING_REJECTIONS = ['other_seller', 'other_product'] as const satisfies readonly PutawayRejection[];
/** The bin's recorded volume or load says no: a supervisor who sees the goods fit may pass it. */
export const CAPACITY_REJECTIONS = ['volume_exceeded', 'weight_exceeded'] as const satisfies readonly PutawayRejection[];
/** The rules a person may pass, recorded with the movement and the putaway task (overridden_rules). */
export const OVERRIDABLE_RULES = [...MIXING_REJECTIONS, ...CAPACITY_REJECTIONS] as const;
export type OverriddenRule = (typeof OVERRIDABLE_RULES)[number];

export const isMixingRejection = (r: PutawayRejection | null | undefined): r is (typeof MIXING_REJECTIONS)[number] =>
  r === 'other_seller' || r === 'other_product';
export const isCapacityRejection = (r: PutawayRejection | null | undefined): r is (typeof CAPACITY_REJECTIONS)[number] =>
  r === 'volume_exceeded' || r === 'weight_exceeded';
/** inactive, count-locked or not a storage bin: never passed */
export const isHardRejection = (r: PutawayRejection): boolean => !isMixingRejection(r) && !isCapacityRejection(r);

/** What else lies in a bin the goods would share (the warning's words). */
export interface BinNeighbours {
  /** codes of the other sellers with stock in the bin */
  readonly sellers: readonly string[];
  /** SKUs of the other products in the bin (another seller's too) */
  readonly skus: readonly string[];
}

/**
 * "Bu gözde BETA satıcısının ürünü var" / "Bu gözde BETA ve GAMA satıcılarının ürünü var" /
 * "Bu gözde başka ürün var (KUPA-2)": the warning a person confirms before mixing a bin.
 */
export function mixedBinWarning(rejection: (typeof MIXING_REJECTIONS)[number], neighbours: BinNeighbours): string {
  if (rejection === 'other_seller' && neighbours.sellers.length > 0) {
    const names = neighbours.sellers.length === 1 ? neighbours.sellers[0]! : `${neighbours.sellers.slice(0, -1).join(', ')} ve ${neighbours.sellers.at(-1)!}`;
    return `Bu gözde ${names} ${neighbours.sellers.length === 1 ? 'satıcısının' : 'satıcılarının'} ürünü var`;
  }
  if (rejection === 'other_product' && neighbours.skus.length > 0) {
    const shown = neighbours.skus.slice(0, 3).join(', ');
    return `Bu gözde başka ürün var (${neighbours.skus.length > 3 ? `${shown} ve ${neighbours.skus.length - 3} ürün daha` : shown})`;
  }
  return PUTAWAY_REJECTION_LABELS[rejection];
}

/** Asked under the warning; the confirming button says "Yine de A-01-2-1 gözüne koy". */
export const MIXED_BIN_QUESTION = 'Yine de bu göze koy?';

export interface PutawaySuggestion {
  readonly locationId: string;
  readonly code: string;
  readonly score: number;
  readonly reasons: readonly PutawayReason[];
  /** Share of the bin volume used after putaway; null when unknown */
  readonly fillRatioAfter: number | null;
}

const isSameItem = (item: PutawayItem, c: BinContent) => c.tenantId === item.tenantId && c.productId === item.productId;

/**
 * Everything that speaks against putting the goods into this bin, in order: the hard rules
 * (pasif, kilitli, not a storage bin: nothing else is asked then), then mixing (another seller,
 * another product where products are kept apart), then capacity. Empty: the goods may go there.
 */
export function putawayProblems(item: PutawayItem, bin: BinSnapshot, policy: PutawayPolicy): PutawayRejection[] {
  if (!bin.isActive) return ['inactive'];
  if (bin.isBlocked) return ['blocked'];
  if (bin.type !== 'pick' && bin.type !== 'bulk') return ['not_storage'];
  const problems: PutawayRejection[] = [];
  if (!policy.allowMixedTenants && bin.contents.some((c) => c.tenantId !== item.tenantId)) problems.push('other_seller');
  if (!policy.allowMixedProducts && bin.contents.some((c) => !isSameItem(item, c))) problems.push('other_product');
  if (
    bin.maxVolumeCm3 !== null &&
    item.unitVolumeCm3 !== null &&
    bin.usedVolumeCm3 + item.unitVolumeCm3 * item.qty > bin.maxVolumeCm3
  ) {
    problems.push('volume_exceeded');
  }
  if (bin.maxWeightG !== null && item.unitWeightG !== null && bin.usedWeightG + item.unitWeightG * item.qty > bin.maxWeightG) {
    problems.push('weight_exceeded');
  }
  return problems;
}

/** Why the goods may not go into this bin at all, or null when they may (the first of putawayProblems). */
export function putawayRejection(item: PutawayItem, bin: BinSnapshot, policy: PutawayPolicy): PutawayRejection | null {
  return putawayProblems(item, bin, policy)[0] ?? null;
}

/** The other sellers and products in a bin the goods would join (the mixing warning's words). */
export function binNeighbours(item: Pick<PutawayItem, 'tenantId' | 'productId'>, contents: readonly (BinContent & { readonly tenantCode: string; readonly sku: string })[]): BinNeighbours {
  const others = contents.filter((c) => !(c.tenantId === item.tenantId && c.productId === item.productId));
  return {
    sellers: [...new Set(others.filter((c) => c.tenantId !== item.tenantId).map((c) => c.tenantCode))].sort(),
    skus: [...new Set(others.map((c) => c.sku))].sort(),
  };
}

/**
 * Scores a bin the goods may enter; higher is better. In order of weight:
 * consolidate with the same lot, then the same product, prefer empty bins over
 * mixing, slot fast movers in pick faces close to the depot (packing) and slow
 * movers in bulk / far bins, keep heavy items low, and fill bins tightly.
 */
export function scoreBin(
  item: PutawayItem,
  bin: BinSnapshot,
  policy: PutawayPolicy,
  depot: Coordinates3D | null,
): Omit<PutawaySuggestion, 'locationId' | 'code'> {
  let score = 0;
  const reasons: PutawayReason[] = [];
  const own = bin.contents.filter((c) => isSameItem(item, c));
  const others = bin.contents.length - own.length;

  if (own.some((c) => c.lotNo === item.lotNo && c.expiryDate === item.expiryDate)) {
    score += 100;
    reasons.push('same_lot');
  } else if (own.length > 0) {
    score += 15;
    reasons.push('same_product');
  } else if (bin.contents.length === 0) {
    score += 40;
    reasons.push('empty_bin');
  }
  // other products in the bin make picking mistakes likelier
  if (others > 0) score -= 30;

  if (bin.type === 'pick' && item.velocity === 'A') {
    score += 20;
    reasons.push('pick_face_for_fast_mover');
  } else if (bin.type === 'bulk' && item.velocity === 'C') {
    score += 20;
    reasons.push('bulk_for_slow_mover');
  } else if (bin.type === 'bulk' && item.velocity === 'A') {
    score -= 20;
  }

  if (depot && bin.coordinates) {
    const distance = Math.abs(bin.coordinates.x - depot.x) + Math.abs(bin.coordinates.y - depot.y);
    const perMetre = item.velocity === 'A' ? -3 : item.velocity === 'B' ? -1 : 0.5;
    score += distance * perMetre;
    if (item.velocity === 'A' && distance <= 6) reasons.push('near_depot');
    if (item.velocity === 'C' && distance >= 8) reasons.push('far_from_depot');
  }

  if ((item.unitWeightG ?? 0) >= policy.heavyItemG) {
    if (bin.level === 1) {
      score += 25;
      reasons.push('heavy_low_level');
    } else {
      score -= 20 * (bin.level - 1);
    }
  } else if (item.velocity === 'A' && bin.level >= policy.goldenLevels[0] && bin.level <= policy.goldenLevels[1]) {
    score += 10;
    reasons.push('golden_zone');
  }

  let fillRatioAfter: number | null = null;
  if (bin.maxVolumeCm3 !== null && item.unitVolumeCm3 !== null) {
    fillRatioAfter = (bin.usedVolumeCm3 + item.unitVolumeCm3 * item.qty) / bin.maxVolumeCm3;
    score += 20 * fillRatioAfter;
    if (fillRatioAfter >= 0.8) reasons.push('fits_tightly');
  }

  return { score: Math.round(score * 100) / 100, reasons, fillRatioAfter };
}

/** Best bins for the goods, highest score first (ties: location code). */
export function suggestPutaway(
  item: PutawayItem,
  bins: readonly BinSnapshot[],
  policy: PutawayPolicy,
  depot: Coordinates3D | null,
  limit = 3,
): PutawaySuggestion[] {
  return bins
    .filter((bin) => putawayRejection(item, bin, policy) === null)
    .map((bin) => ({ locationId: bin.locationId, code: bin.code, ...scoreBin(item, bin, policy, depot) }))
    .sort((a, b) => b.score - a.score || a.code.localeCompare(b.code))
    .slice(0, limit);
}
